import { AppFooter } from 'components';
import { HOW_TO_EARN_REWARDS_PATH, isWebsite as functionIsWebsite } from 'core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// SCREEN COMPONENTS
import {
  AthleteZone,
  Conference,
  Event,
  FanZone,
  GetTheMobileApp,
  Landing,
  News,
  NewsArticle,
  OurAthletes,
  OurStory,
  Partners
} from 'screens/Website';
import {
  ChildSafetyStandard,
  CommunityGuidelines,
  PrivacyPolicy,
  StopTheHate,
  TermsAndConditions
} from 'screens/Website/FooterPages';

import { Footer, Header } from 'screens/Website/Home/components';

import { Header as AppHeader } from 'components';

import CTAScreenV2 from 'screens/CTAScreenV2';
import RateIt from 'screens/RateIt';
import ForgotPassword from 'screens/ForgotPassword';
import GetTheApp from 'screens/GetTheApp';
import Login from 'screens/Login';
import Post from 'screens/Post';
import Profile from 'screens/Profile';
import SignUp from 'screens/SignUp';
import { MigratedUser, ValidationEmailSent, VerifyEmail } from 'screens/ValidateEmail/screens';
import SubscriptionPlansScreen from 'screens/SubscriptionPlans';
import { PaymentFailure, PaymentSuccess } from 'screens/SubscriptionResult';
import PaymentMethodSelection from 'screens/PaymentMethodSelection';

import { BallBg } from 'components';
import { CTA } from 'lib';
import DeepLink from 'screens/DeepLink';
import FanCamChallenge from 'screens/FanCamChallenge';
import EmailView from 'screens/EmailView';
import OpenApp from 'screens/OpenApp';
import P4PDay from 'screens/P4PDay';
import Rerouter from 'screens/Rerouter';
import SignInFirst from './SignInFirst';

const authPaths = [
  '/signup',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/activate-account',
  '/validation-email-sent'
];

/** Pages an email lands a fan on. Each is the whole screen, with no app header or footer. */
const EMAIL_LANDING_PATHS = ['/open-app', `/${HOW_TO_EARN_REWARDS_PATH}`, '/email/view'];

// USER ROUTES
const NotUser = () => {
  const [showGetTheApp, setShowGetTheApp] = useState(true),
    location = useLocation(),
    isWebsite = functionIsWebsite(location.pathname),
    isAuth = authPaths.includes(location.pathname),
    // CTA flow screens (/shoutout/:username/...) render full-screen without the app header
    // CTA and Rate It flows render full-screen without the app header. No trailing slash on
    // `/rate-it`: unlike `/shoutout` there is no sibling feed route for the bare path to protect.
    hideAppHeader =
      location.pathname.startsWith('/shoutout/') ||
      location.pathname.startsWith('/rate-it') ||
      EMAIL_LANDING_PATHS.includes(location.pathname),
    currentUser = useSelector((state) => state.user),
    dispatch = useDispatch(),
    { t, i18n } = useTranslation(),
    navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.includes('billing') && !currentUser) {
      navigate(`/login${location.search}`);

      return;
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <React.Fragment>
      {isWebsite && (
        <React.Fragment>
          <Header />
          <BallBg />
        </React.Fragment>
      )}

      {!isWebsite && !isAuth && !hideAppHeader && <AppHeader />}

      <Routes>
        <Route path="/" element={<Landing />} />

        <Route path="subscription-plans" element={<SubscriptionPlansScreen />} />

        <Route path="athlete-zone" element={<AthleteZone />} />

        <Route path="fan-zone" element={<FanZone />} />

        <Route path="our-athletes" element={<OurAthletes />} />

        <Route path="partners-and-sponsors" element={<Partners />} />

        <Route path="our-story" element={<OurStory />} />

        <Route path="event" element={<Event />} />

        <Route path="rerouter" element={<Rerouter />} />

        <Route path="news" element={<News />} />

        <Route path="signup" element={<SignUp />} />

        <Route path="login" element={<Login />} />

        <Route path="/home" element={<SignUp />} />

        <Route path="forgot-password" element={<ForgotPassword />} />

        <Route path="reset-password" element={<ForgotPassword />} />

        <Route path="activate-account" element={<MigratedUser />} />

        <Route path="terms-and-conditions" element={<TermsAndConditions />} />

        <Route path="privacy-policy" element={<PrivacyPolicy />} />

        <Route path="stop-the-hate" element={<StopTheHate />} />

        <Route path="child-safety-standard-policy" element={<ChildSafetyStandard />} />

        <Route path="community-guidelines" element={<CommunityGuidelines />} />

        <Route path="validation-email-sent" element={<ValidationEmailSent />} />

        <Route path="verify-email/:emailToken" element={<VerifyEmail />} />

        <Route path="articles/:articleId" element={<NewsArticle />} />

        <Route path="/conference/:roomId" element={<Conference />} />

        <Route path="/post/:postId" element={<Post />} />

        <Route path="/shared/post/:postId" element={<Post />} />

        <Route path="/profile/:username/:cta/:uuid" element={<Profile />} />
        <Route path="/profile/:username/:cta" element={<Profile />} />

        <Route path="/profile/:username" element={<Profile />} />

        <Route path="/shoutout/:username/*" element={<CTAScreenV2 ctaType={CTA.SEND_SHOUTOUT} />} />

        {/* Registered here as well as in `User.js` on purpose: a referral link is overwhelmingly
            opened signed-out, and `containers/Auth.js` swaps the whole tree on `user._id`, so a
            route in only one container gives the other half a blank page. A guest can browse and
            scroll the feed — the feed endpoint is public — and is asked to sign in only when they
            click a tile. */}
        <Route path="/rate-it/:tiriId/*" element={<RateIt />} />

        <Route path="/get-the-mobile-app" element={<GetTheMobileApp />} />

        {/* An email's "Open the App" button and its "View in browser" link. Full-screen, and in
            both containers: a fan arriving from an email may be signed in or not. */}
        <Route path="/open-app" element={<OpenApp />} />
        <Route
          path={`/${HOW_TO_EARN_REWARDS_PATH}`}
          element={<OpenApp appPath={HOW_TO_EARN_REWARDS_PATH} />}
        />

        <Route path="/email/view" element={<EmailView />} />

        {/* The Password Changed and New Device emails' "Review Account Activity". Signed out, it
            rendered a blank page; it now asks the fan to sign in and then opens Settings. */}
        <Route path="/settings" element={<SignInFirst />} />

        <Route path="/l/:shortCode" element={<DeepLink />} />

        {/* Where a campaign email's primary button lands. Declared in both containers:
            a fan arriving from an email is usually signed out, and a route present in only
            one of the two works for half the audience and 404s for the other half. */}
        <Route path="/fancam/:id" element={<FanCamChallenge />} />

        <Route path="/p4pday" element={<P4PDay />} />
      </Routes>

      <GetTheApp />

      {isWebsite && <Footer />}

      {showGetTheApp && !isWebsite && !hideAppHeader && (
        <AppFooter handleClose={() => setShowGetTheApp(false)} />
      )}
    </React.Fragment>
  );
};

export default NotUser;
